
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { fetchReagents } from "@/services/reagentApi";
import LatestReagentsList from "./LatestReagentsList";
import { useAuth } from "@/contexts/auth";

const LatestReagentsSection = () => {
  const { user, isLoading: authLoading } = useAuth();
  
  const { 
    data: reagents = [], 
    isLoading,
    isFetching,
    error,
    refetch
  } = useQuery({
    queryKey: ['latestReagents'],
    queryFn: fetchReagents,
    staleTime: 60 * 1000, // 1 minute
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attempt) => Math.min(attempt > 1 ? 3000 : 1000, 30 * 1000),
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    enabled: !authLoading && !!user,
  });

  // Show only the most recent 4 reagents
  const latestReagents = reagents.slice(0, 4);
  
  // Only log on initial load, not on every render
  useEffect(() => {
    if (!isLoading && !isFetching && reagents.length > 0 && user) {
      console.log(`LatestReagentsSection loaded ${reagents.length} reagents`);
    }
  }, [isLoading, isFetching, reagents.length, user]);

  // Fetch once after auth is loaded
  useEffect(() => {
    if (!authLoading && user) {
      refetch();
    }
  }, [authLoading, user, refetch]);

  return (
    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Latest Reagents</h2>
          <div className="flex space-x-4">
            {user && (
              <Button asChild>
                <Link to="/reagents/new">Upload Reagent</Link>
              </Button>
            )}
            <Button asChild variant="outline">
              <Link to="/reagents">View All</Link>
            </Button>
          </div>
        </div>
        
        <LatestReagentsList 
          reagents={latestReagents} 
          isLoading={isLoading || isFetching || authLoading}
          error={error as Error | null}
          refetch={refetch}
        />
      </div>
    </section>
  );
};

export default LatestReagentsSection;
